import React, { useState, useEffect } from "react";
import Card from "../components/ui/Card";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import Header from "../components/layout/Header";
import { useToast } from "../hooks/useToast";
import {
  getTotalVehicles,
  getTotalOutstanding,
  getMonthlyInvoices,
  getMonthlyPayments,
  getMonthlyWriteOffs
} from "../services/api";

const ReportsPageImproved: React.FC = () => {
  const [totalVehicles, setTotalVehicles] = useState<number | null>(null);
  const [totalOutstanding, setTotalOutstanding] = useState<number | null>(null);
  const [monthlyInvoices, setMonthlyInvoices] = useState<number | null>(null);
  const [monthlyPayments, setMonthlyPayments] = useState<number | null>(null);
  const [monthlyWriteOffs, setMonthlyWriteOffs] = useState<number | null>(null);
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const { showToast, ToastContainer } = useToast();

  const fetchReportData = async () => {
    setLoading(true);
    try {
      // Fetch dashboard stats
      const [vehicles, outstanding, invoices, payments, writeOffs] = await Promise.all([
        getTotalVehicles(),
        getTotalOutstanding(),
        getMonthlyInvoices(month, year),
        getMonthlyPayments(month, year),
        getMonthlyWriteOffs(month, year)
      ]);

      setTotalVehicles(vehicles.totalVehicles ?? vehicles.total ?? 0);
      setTotalOutstanding(outstanding.totalOutstandingBalance ?? outstanding.total ?? 0);
      setMonthlyInvoices(invoices.totalInvoiceAmount ?? invoices.count ?? 0);
      setMonthlyPayments(payments.totalPaymentAmount ?? payments.count ?? 0);
      setMonthlyWriteOffs(writeOffs.totalWriteOffAmount ?? writeOffs.count ?? 0);
    } catch (error: any) {
      console.error("Error fetching report data:", error);
      showToast(error?.message || "Failed to fetch report data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [month, year]);

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 to-indigo-100">
      <Header title="Reports Dashboard" showBackButton={true} />
      
      <main className="w-full max-w-7xl mx-auto p-3 sm:p-4 md:p-6 space-y-6 md:space-y-8">
        {/* Top Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          <Card className="p-4 md:p-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm md:text-base font-medium opacity-90">Total Vehicles</h3>
                <p className="text-2xl md:text-3xl lg:text-4xl font-bold mt-1 md:mt-2">
                  {loading ? <LoadingSpinner size="sm" color="white" /> : totalVehicles}
                </p>
              </div>
              <div className="text-3xl md:text-4xl lg:text-5xl opacity-80">🚗</div>
            </div>
          </Card>

          <Card className="p-4 md:p-6 bg-gradient-to-r from-green-500 to-green-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm md:text-base font-medium opacity-90">Outstanding Balance</h3>
                <p className="text-2xl md:text-3xl lg:text-4xl font-bold mt-1 md:mt-2">
                  {loading ? <LoadingSpinner size="sm" color="white" /> : `₹${totalOutstanding?.toLocaleString()}`}
                </p>
              </div>
              <div className="text-3xl md:text-4xl lg:text-5xl opacity-80">💰</div>
            </div>
          </Card>
        </div>

        {/* Monthly Overview */}
        <Card className="p-4 md:p-6 bg-white shadow-lg">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6">Monthly Overview</h2>
          
          {/* Month/Year Selectors */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-4 md:mb-6">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
              <select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="w-full p-2 md:p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="w-full p-2 md:p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Monthly Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            <Card className="p-4 md:p-6 bg-gradient-to-r from-purple-500 to-purple-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm md:text-base font-medium opacity-90">Monthly Invoices</h3>
                  <p className="text-xl md:text-2xl lg:text-3xl font-bold mt-1 md:mt-2">
                    {loading ? <LoadingSpinner size="sm" color="white" /> : `₹${monthlyInvoices?.toLocaleString()}`}
                  </p>
                </div>
                <div className="text-2xl md:text-3xl lg:text-4xl opacity-80">📄</div>
              </div>
            </Card>

            <Card className="p-4 md:p-6 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm md:text-base font-medium opacity-90">Monthly Payments</h3>
                  <p className="text-xl md:text-2xl lg:text-3xl font-bold mt-1 md:mt-2">
                    {loading ? <LoadingSpinner size="sm" color="white" /> : `₹${monthlyPayments?.toLocaleString()}`}
                  </p>
                </div>
                <div className="text-2xl md:text-3xl lg:text-4xl opacity-80">💳</div>
              </div>
            </Card>

            <Card className="p-4 md:p-6 bg-gradient-to-r from-red-500 to-red-600 text-white sm:col-span-2 lg:col-span-1">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm md:text-base font-medium opacity-90">Monthly Write-offs</h3>
                  <p className="text-xl md:text-2xl lg:text-3xl font-bold mt-1 md:mt-2">
                    {loading ? <LoadingSpinner size="sm" color="white" /> : `₹${monthlyWriteOffs?.toLocaleString()}`}
                  </p>
                </div>
                <div className="text-2xl md:text-3xl lg:text-4xl opacity-80">✍️</div>
              </div>
            </Card>
          </div>
        </Card>
      </main>

      <ToastContainer />
    </div>
  );
};

export default ReportsPageImproved;
